
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  forwardRef,
  input,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

@Component({
    selector: 'app-input-textarea',
    host: {
        '[attr.id]': 'null'
    },
    imports: [ReactiveFormsModule],
    templateUrl: './input-textarea.component.html',
    styleUrl: './input-textarea.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputTextareaComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => InputTextareaComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InputTextareaComponent implements OnInit, OnChanges {
  id = input.required<string>();

  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() rows: number = 3;
  @Input() maxLength?: number;
  @Input() controlName!: AbstractControl<any, any> | null;
  @Input() customErrors: { [key: string]: string } = {};

  value: string = '';
  private baseValidator: ValidatorFn | null = null;

  ngOnInit(): void {
    this.captureBaseValidator();
    this.updateControlState();
    this.updateValidators();
    this.syncValueFromControl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['controlName']) {
      this.captureBaseValidator();
      this.syncValueFromControl();
    }

    if (changes['disabled']) {
      this.updateControlState();
    }

    if (changes['maxLength'] || changes['controlName']) {
      this.updateValidators();
    }
  }

  private captureBaseValidator(): void {
    if (!(this.controlName instanceof FormControl)) return;
    this.baseValidator = this.controlName.validator ?? null;
  }

  private updateControlState(): void {
    if (!(this.controlName instanceof FormControl)) return;
    this.disabled ? this.controlName.disable() : this.controlName.enable();
  }

  private updateValidators(): void {
    if (!(this.controlName instanceof FormControl)) return;

    const validators: ValidatorFn[] = [];
    if (this.baseValidator) validators.push(this.baseValidator);
    if (this.maxLength !== undefined) validators.push(Validators.maxLength(this.maxLength));

    this.controlName.setValidators(validators);
    this.controlName.updateValueAndValidity({ emitEvent: false });
  }

  private syncValueFromControl(): void {
    if (!(this.controlName instanceof FormControl)) return;
    this.value = String(this.controlName.value ?? '');
  }

  get control(): FormControl {
    return this.controlName as FormControl;
  }

  onChange = (value: string) => {};
  onTouched = () => {};

  writeValue(value: string): void {
    this.value = value ?? '';
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.updateControlState();
  }

  validate(_: AbstractControl): ValidationErrors | null {
    return null;
  }

  onInput(event: Event): void {
    const inputElement = event.target as HTMLTextAreaElement;
    const inputValue = inputElement.value;
    this.value = inputValue;
    this.onChange(inputValue);
    this.onTouched();
  }

  get isTouched(): boolean {
    return this.controlName instanceof FormControl ? this.controlName.touched : false;
  }

  get errorMessage(): string {
    if (!(this.controlName instanceof FormControl) || !this.isTouched || !this.controlName.errors) {
      return '';
    }

    const errors = {
      ...(this.controlName.errors || {}),
      ...(this.controlName.parent?.errors || {}),
    };

    for (const errorName of Object.keys(errors)) {
      if (this.customErrors[errorName]) return this.customErrors[errorName];

      if (errorName === 'required') {
        return `El campo ${this.label.toLowerCase()} es requerido.`;
      }
      if (errorName === 'maxlength') {
        return `El campo ${this.label.toLowerCase()} debe tener máximo ${errors['maxlength'].requiredLength} caracteres.`;
      }
    }

    return '';
  }
}
